import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Download, Eye, Calendar, Film, Printer, Paperclip } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtFCFA = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-FR').replace(/,/g, ' ')} FCFA`;
const fmtSize = (b) => (!b ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`);
const clientOf = (c) => {
  const v = c.entreprise || c.client_nom || c.prospect_entreprise || c.client_name || c.nom_client || c.client;
  return (v && typeof v === 'object' ? (v.nom || v.entreprise || v.name) : v) || '—';
};

// Types de documents CRM partageables en PDF
export const DOC_KINDS = {
  contract: { label: 'Contrat', list: '/api/contrats', pdf: (id) => `/api/contrats/${id}/pdf`, color: 'blue', legacyId: 'contract_id',
    norm: (c) => ({ numero: c.numero_contrat || c.numero || '—', client_nom: clientOf(c), montant: c.montant_total || c.montant || 0, statut: c.statut || '', date_fin: c.date_fin || '' }) },
  devis: { label: 'Devis', list: '/api/devis', pdf: (id) => `/api/devis/${id}/pdf`, color: 'cyan', legacyId: 'devis_id',
    norm: (c) => ({ numero: c.numero_devis || c.numero || '—', client_nom: clientOf(c), montant: c.total_ttc || c.montant || 0, statut: c.statut || '' }) },
  devis_conception: { label: 'Devis conception', list: '/api/conception/devis', pdf: (id) => `/api/conception/devis/${id}/pdf`, color: 'orange',
    norm: (c) => ({ numero: c.numero || '—', client_nom: c.client_nom || '—', montant: c.total_ttc || 0, statut: c.statut || '' }) },
  facture: { label: 'Facture', list: '/api/factures', pdf: (id) => `/api/factures/${id}/pdf`, color: 'rose', legacyId: 'facture_id',
    norm: (c) => ({ numero: c.numero_facture || c.numero || '—', client_nom: clientOf(c), montant: c.total_ttc || c.montant_ttc || 0, statut: c.statut || '' }) },
  reservation: { label: 'Réservation', list: '/api/emplacement-reservations', pdf: (id) => `/api/emplacement-reservations/${id}/pdf-file`, color: 'indigo', legacyId: 'reservation_id',
    norm: (c) => ({ numero: c.numero || c.emplacement_nom || (c.id || '').slice(0, 8), client_nom: clientOf(c), montant: c.montant || 0, statut: c.statut || '' }) },
  bon_commande: { label: 'Bon de commande', list: '/api/commandes', pdf: (id) => `/api/commandes/${id}/pdf`, color: 'teal',
    norm: (c) => ({ numero: c.numero || c.objet || (c.id || '').slice(0, 8), client_nom: c.fournisseur_nom || '—', montant: c.total_ttc || 0, statut: c.statut || '' }) },
  liste_commande: { label: 'Liste de commande', list: '/api/listes-commande', pdf: (id) => `/api/listes-commande/${id}/pdf`, color: 'emerald',
    norm: (c) => ({ numero: c.numero || '—', client_nom: c.client_nom || '—', montant: 0, statut: '' }) },
};

const COLORS = {
  blue: ['bg-blue-50 border-blue-200', 'bg-blue-600 hover:bg-blue-700', 'text-blue-700'],
  cyan: ['bg-cyan-50 border-cyan-200', 'bg-cyan-600 hover:bg-cyan-700', 'text-cyan-700'],
  orange: ['bg-orange-50 border-orange-200', 'bg-orange-500 hover:bg-orange-600', 'text-orange-700'],
  rose: ['bg-rose-50 border-rose-200', 'bg-rose-600 hover:bg-rose-700', 'text-rose-700'],
  indigo: ['bg-indigo-50 border-indigo-200', 'bg-indigo-600 hover:bg-indigo-700', 'text-indigo-700'],
  teal: ['bg-teal-50 border-teal-200', 'bg-teal-600 hover:bg-teal-700', 'text-teal-700'],
  emerald: ['bg-emerald-50 border-emerald-200', 'bg-emerald-600 hover:bg-emerald-700', 'text-emerald-700'],
};

export const buildDocAttachment = (kind, item) => {
  const k = DOC_KINDS[kind];
  const n = k.norm(item);
  const att = { doc_id: item.id, ...n, filename: `${k.label.replace(/ /g, '_')}_${String(n.numero).replace(/[^\w-]+/g, '_')}.pdf` };
  if (k.legacyId) att[k.legacyId] = item.id;
  return att;
};

const saveBlob = (blob, filename, mode) => {
  const url = URL.createObjectURL(blob);
  if (mode === 'view') { window.open(url, '_blank'); return; }
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

export const openDocPdf = async (kind, id, filename, mode = 'view') => {
  const k = DOC_KINDS[kind];
  if (!k || !id) return toast.error('Document introuvable');
  try {
    const r = await axios.get(`${API}${k.pdf(id)}`, { responseType: 'blob' });
    saveBlob(new Blob([r.data], { type: 'application/pdf' }), filename || `${kind}.pdf`, mode);
  } catch { toast.error('Impossible de récupérer le PDF'); }
};

export const downloadStoredFile = async (path, filename, mode = 'download') => {
  try {
    const r = await axios.get(`${API}/api/messagerie/files/${path}`, { responseType: 'blob' });
    saveBlob(r.data, filename || 'fichier', mode);
  } catch { toast.error('Téléchargement impossible'); }
};

const Time = ({ ts }) => {
  let t = '';
  try { t = new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch { /* */ }
  return <span className="text-[10px] text-slate-400">{t}</span>;
};

function PdfRow({ name, sub, onClick, testid }) {
  return (
    <button onClick={onClick} className="w-full flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-3 py-2.5 hover:border-slate-400 transition-colors text-left" data-testid={testid}>
      <div className="w-10 h-12 rounded-md bg-red-500 text-white flex flex-col items-center justify-center flex-shrink-0">
        <FileText className="h-4 w-4" />
        <span className="text-[8px] font-black mt-0.5">PDF</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-bold text-slate-900 truncate">{name}</div>
        <div className="text-[11px] text-slate-500">{sub}</div>
      </div>
      <Download className="h-4 w-4 text-slate-400" />
    </button>
  );
}

export function DocMessage({ msg }) {
  const a = msg.attachment || {};
  const kind = msg.type;
  const k = DOC_KINDS[kind] || DOC_KINDS.devis;
  const [box, btn, txt] = COLORS[k.color];
  const id = a.doc_id || (k.legacyId && a[k.legacyId]);
  const filename = a.filename || `${k.label}_${a.numero || ''}.pdf`;
  return (
    <div className={`w-[340px] max-w-[85%] border rounded-2xl p-4 text-sm shadow-sm ${box}`} data-testid={`rich-${kind}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-extrabold text-slate-900 text-base truncate" data-testid={`rich-${kind}-company`}>{a.client_nom || '—'}</div>
          <div className={`text-xs font-semibold ${txt}`}>{k.label} {a.numero}</div>
        </div>
        <Time ts={msg.created_at} />
      </div>
      {(a.montant > 0 || a.statut) && (
        <div className="flex items-center justify-between text-xs mt-2">
          <span className="font-bold text-slate-800">{a.montant > 0 ? fmtFCFA(a.montant) : ''}</span>
          {a.statut && <span className="px-2 py-0.5 rounded bg-white/70 text-slate-700 font-semibold text-[10px]">{String(a.statut).replace(/_/g, ' ')}</span>}
        </div>
      )}
      <div className="mt-3">
        <PdfRow name={filename} sub="Document PDF · cliquer pour télécharger" onClick={() => openDocPdf(kind, id, filename, 'download')} testid={`btn-file-${kind}-pdf`} />
      </div>
      <div className="flex gap-2 mt-2">
        <button onClick={() => openDocPdf(kind, id, filename, 'view')} className={`flex-1 h-8 px-3 ${btn} text-white text-xs font-semibold rounded-md flex items-center justify-center gap-1`} data-testid={`btn-view-${kind}-pdf`}>
          <Eye className="h-3.5 w-3.5" /> Visualiser
        </button>
        <button onClick={() => openDocPdf(kind, id, filename, 'download')} className="h-8 px-3 bg-white border border-slate-300 text-slate-700 text-xs font-semibold rounded-md flex items-center justify-center gap-1" data-testid={`btn-download-${kind}-pdf`}>
          <Download className="h-3.5 w-3.5" /> Télécharger
        </button>
      </div>
    </div>
  );
}

export function FileMessage({ msg }) {
  const a = msg.attachment || {};
  const isPdf = (a.content_type || '').includes('pdf');
  return (
    <div className="w-[320px] max-w-[85%] bg-slate-50 border border-slate-200 rounded-2xl p-3 shadow-sm" data-testid="rich-file">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-slate-600 flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" /> Fichier</span>
        <Time ts={msg.created_at} />
      </div>
      {isPdf
        ? <PdfRow name={a.filename} sub={fmtSize(a.size)} onClick={() => downloadStoredFile(a.path, a.filename)} testid="btn-download-file" />
        : (
          <button onClick={() => downloadStoredFile(a.path, a.filename)} className="w-full flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-3 py-2.5 hover:border-slate-400 text-left" data-testid="btn-download-file">
            <div className="w-10 h-10 rounded-md bg-slate-700 text-white flex items-center justify-center"><Paperclip className="h-4 w-4" /></div>
            <div className="min-w-0 flex-1"><div className="text-xs font-bold truncate">{a.filename}</div><div className="text-[11px] text-slate-500">{fmtSize(a.size)}</div></div>
            <Download className="h-4 w-4 text-slate-400" />
          </button>
        )}
    </div>
  );
}

export function PrintJobMessage({ msg }) {
  const a = msg.attachment || {};
  const isVideo = a.media_type === 'video';
  return (
    <div className="w-[360px] max-w-[90%] bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm shadow-sm" data-testid="rich-print-job">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">{isVideo ? <Film className="h-4 w-4" /> : <Printer className="h-4 w-4" />}</div>
          <div className="min-w-0">
            <div className="font-extrabold text-emerald-900 truncate">{isVideo ? 'Vidéos à diffuser' : 'Affiches à imprimer'}</div>
            <div className="text-xs text-slate-600 truncate">{a.title}{a.emplacement_nom ? ` · ${a.emplacement_nom}` : ''}</div>
          </div>
        </div>
        <Time ts={msg.created_at} />
      </div>
      <div className="mt-3">
        <PdfRow name={a.pdf_name} sub={`${fmtSize(a.pdf_size)} · ${isVideo ? 'récapitulatif' : 'prêt pour impression'}`} onClick={() => downloadStoredFile(a.pdf_path, a.pdf_name)} testid="btn-download-print-pdf" />
      </div>
      {(a.videos || []).length > 0 && (
        <div className="mt-2 space-y-1.5" data-testid="print-job-videos">
          {a.videos.map((v) => (
            <button key={v.path} onClick={() => downloadStoredFile(v.path, v.name || `video_${v.num}.mp4`)} className="w-full flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 hover:border-emerald-400 text-left" data-testid={`btn-download-video-${v.num}`}>
              <Film className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <span className="text-xs font-semibold text-slate-800 truncate flex-1">#{v.num} {v.name || 'vidéo'}{v.company ? ` · ${v.company}` : ''}</span>
              <Download className="h-3.5 w-3.5 text-slate-400" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function PlanningMessage({ msg }) {
  const a = msg.attachment || {};
  const items = a.items || [];
  return (
    <div className="w-[380px] max-w-[90%] bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm shadow-sm" data-testid="rich-planning">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><Calendar className="h-4 w-4" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-amber-900">Planning · Semaine {a.week_number}</div>
          {a.owner && <div className="text-[11px] text-slate-600 truncate">{a.owner}{a.date_from ? ` · du ${new Date(a.date_from).toLocaleDateString('fr-FR')} au ${new Date(a.date_to).toLocaleDateString('fr-FR')}` : ''}</div>}
        </div>
        <Time ts={msg.created_at} />
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-500 bg-white rounded-lg p-3">Aucune activité prévue cette semaine.</div>
      ) : (
        <div className="space-y-1.5 max-h-56 overflow-y-auto">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2 bg-white rounded-lg px-2.5 py-1.5 border border-slate-100">
              <span className="w-1.5 h-8 rounded-full flex-shrink-0" style={{ background: it.color || '#3b82f6' }} />
              <div className="w-14 flex-shrink-0">
                <div className="text-[10px] font-bold text-slate-500 uppercase">{it.day}</div>
                <div className="text-[10px] text-slate-400">{it.time}</div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-800 truncate">{it.title}</div>
                {it.who && <div className="text-[10px] text-slate-500 truncate">{it.who}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      <a href="/planning" className="mt-3 h-8 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-md flex items-center justify-center w-fit" data-testid="btn-open-planning">Ouvrir le planning</a>
    </div>
  );
}
